import React from 'react';
import Card from '../../common/Card/Card';
import styles from './OverviewSections.module.css';

/**
 * "What You'll Need to Submit": one expandable row per stage listing its
 * deliverables. The current stage starts open; the others start closed.
 * Renders nothing when there are no stages.
 *
 * Built on <details>/<summary> so it is keyboard-accessible without any state.
 * The caller should give this component a `key` that changes with the
 * competition, otherwise a row the student opened stays open on the next one.
 */
const DeliverablesAccordion = ({ stages, currentStageKey }) => {
  const ordered = [...(stages || [])].sort((a, b) => (a.number || 0) - (b.number || 0));
  if (!ordered.length) return null;

  return (
    <Card title="What You'll Need to Submit">
      <p className={styles.intro}>
        Expand a stage to see its deliverables. Full question instructions are inside your
        application workspace.
      </p>
      <div className={styles.accordion}>
        {ordered.map((stage) => {
          const deliverables = stage.deliverables || [];
          return (
            <details
              key={stage.key}
              className={styles.accordionItem}
              open={stage.key === currentStageKey}
            >
              <summary className={styles.accordionHead}>
                <span className={styles.accordionTitle}>
                  Stage {stage.number} — {stage.name}
                  {stage.isPlaceholderName && <span className={styles.placeholderTag}>Placeholder name</span>}
                </span>
                <span className={styles.accordionCount}>
                  {deliverables.length} deliverable{deliverables.length === 1 ? '' : 's'}
                </span>
                <span className={styles.chevron} aria-hidden="true">▾</span>
              </summary>

              {deliverables.length === 0 ? (
                <p className={styles.noDeliverables}>Deliverables will appear here once they are added.</p>
              ) : (
                <div className={styles.deliverables}>
                  {deliverables.map((d, i) => (
                    <div key={`${d.title}-${i}`} className={styles.deliverable}>
                      <div className={styles.deliverableTop}>
                        <span className={styles.deliverableTitle}>{d.title}</span>
                        {d.requirement && (
                          <span className={styles.deliverableRequirement}>{d.requirement}</span>
                        )}
                      </div>
                      {d.note && <p className={styles.deliverableNote}>{d.note}</p>}
                    </div>
                  ))}
                </div>
              )}
            </details>
          );
        })}
      </div>
    </Card>
  );
};

export default DeliverablesAccordion;
